<template>
	<div class="col p-0 content-header-filters" style="row-gap: 1rem">
		<div class="px-1">
			<DepartmentSelect
				:style="{ width: props.width || '16.5rem' }"
				ref="departmentSelect"
				v-model="department.selected"
				:terms="terms"
				@option:selected="(selected) => departmentSelected(selected)"
				:selectable="filteredDepartments"
			>
			</DepartmentSelect>
		</div>
		<div class="px-1">
			<EmployeeSelect
				:style="{ width: props.width || '16.5rem' }"
				ref="employeeSelect"
				v-model="employee.selected"
				@option:selected="(selected) => employeeSelected(selected)"
			></EmployeeSelect>
		</div>
		<div class="px-1" v-for="filter in validFilters" :key="filter">
			<component
				:is="getComponent(filter)"
				v-model="getModel(filter).selected"
				:options="getOptions(filter)"
				:loading="getLoading(filter)"
				:selectable="getSelectable(filter)"
				@option:selected="
					(selected) => handleSelected(filter, selected)
				"
				@open="handleOpen(filter)"
				:style="{ width: props.width || '16.5rem' }"
				searchable
				:placeholder="getPlaceholder(filter)"
			></component>
		</div>
		<slot name="picker"></slot>
		<slot name="search"></slot>
	</div>
</template>
<script setup>
import Select from "@elements/select/BaseSingleSelect.vue";
import CategorySelect from "@elements/select/CategorySingleSelect.ce.vue";
import DepartmentSelect from "@elements/select/single/DepartmentSelect.ce.vue";
import EmployeeSelect from "@elements/select/single/EmployeeSelect.vue";
import StudentSelect from "@elements/select/single/StudentSelect.vue";
import StudentService from "@services/StudentService";
import { computed, defineProps, onMounted, ref, watch, watchEffect } from "vue";
import { terms as getTerms } from "../../helpers/TermHelper";
const studentService = new StudentService();

const props = defineProps({
	filters: Array,
	width: String,
	// Data
	terms: Object,
	modulePermissions: Object,
	studentId: Number,
	students: Array,
	departments: Array,
	// State
	tab: String,
	studentsLoading: Boolean,
	categoriesLoading: Boolean,
	themesLoading: Boolean,
	plansLoading: Boolean,
	templatesLoading: Boolean,
	// Options
	departmentOptions: Array,
	employeeOptions: Array,
	studentOptions: Array,
	categoryOptions: Array,
	planOptions: Array,
	themeOptions: Array,
	templateOptions: Array,
	typeOptions: Array,
});

const terms = ref(props.terms || {});

const filteredDepartmentOptions = ref([]);
const studentDepartments = ref([]);

const departmentSelect = ref(null);
const employeeSelect = ref(null);

const hasDepartments = computed(
	() => props.departments && props.departments.length > 0,
);

const department = ref({
	options: [
		{
			label: `Alle ${terms.value.department.multi}`,
			option: "",
		},
	],
	selected: {
		label: computed(() => `Alle ${terms.value.department.multi}`),
		option: "",
	},
	isLoading: false,
});

const employee = ref({
	options: [
		{
			label: "Alle medarbejdere",
			option: "",
		},
	],
	selected: {
		label: "Alle medarbejdere",
		option: "",
		dep_ids: [],
	},
});

const student = ref({
	options: [
		{
			label: `Alle ${terms.value.student.multi}`,
			option: "",
		},
	],
	selected: {
		label: computed(() => `Alle ${terms.value.student.multi}`),
		option: "",
	},
	dep_ids: [],
});

const category = ref({
	options: [
		{
			label: "Alle overskrifter",
			option: 0,
		},
	],
	selected: {
		label: "Alle overskrifter",
		option: 0,
	},
});

const plan = ref({
	options: [
		{
			label: "Alle planer",
			option: 0,
		},
	],
	selected: {
		label: "Alle planer",
		option: 0,
	},
});

const theme = ref({
	options: [
		{
			label: computed(() => {
				if (props.filters.includes("type")) {
					if (journalType.value.selected.option === "all") {
						return `Alle temaer/målepunkter`;
					} else if (journalType.value.selected.option === 2) {
						return `Alle målepunkter`;
					}
				}
				return props.tab === "all"
					? `Alle temaer/målepunkter`
					: `Alle ${props.tab === 2 ? "målepunkter" : "temaer"}`;
			}),
			option: 0,
		},
	],
	selected: {
		label: computed(() => {
			if (props.filters.includes("type")) {
				if (journalType.value.selected.option === "all") {
					return `Alle temaer/målepunkter`;
				} else if (journalType.value.selected.option === 2) {
					return `Alle målepunkter`;
				}
			}
			return props.tab === "all"
				? `Alle temaer/målepunkter`
				: `Alle ${props.tab === 2 ? "målepunkter" : "temaer"}`;
		}),
		option: 0,
	},
});

const template = ref({
	options: [
		{
			label: "Alle dataregistreringer",
			option: 0,
		},
	],
	selected: {
		label: "Alle dataregistreringer",
		option: 0,
	},
});

const journalType = ref({
	options: [
		{
			label: "Alle journaltyper",
			option: "all",
		},
	],
	selected: {
		label: "Alle journaltyper",
		option: "all",
	},
});

onMounted(async () => {
	if (!props.terms) {
		terms.value = await getTerms();
	}
	if (props.studentId) {
		const res = await studentService.get(props.studentId);
		student.value.selected = {
			label: `${res?.fname} ${res?.lname}`,
			option: res.student_id,
		};
		student.value.dep_ids = res.department_ids.split(",").map(Number);
		emit("student:selected", student.value.selected);
	}
});

const filteredDepartments = (option) => {
	if (
		student.value.selected.option !== "" &&
		student.value.dep_ids.length > 0
	) {
		return (
			student.value.dep_ids.includes(option.option) ||
			option.option === ""
		);
	}
	return true;
};

const filteredCategories = (option) => {
	const depIdsArray = option.dep_ids || [];

	const depIncluded =
		depIdsArray.some((depId) => student.value.dep_ids.includes(depId)) ||
		depIdsArray.some((depId) =>
			employee.value.selected.dep_ids.includes(depId),
		) ||
		depIdsArray.includes(department.value.selected.option);

	const optionHasDeps = option.dep_ids && option.dep_ids.length > 0;

	return (
		(option.type_id === props.tab ||
			props.tab === "all" ||
			option.option === 0) &&
		!option.deleted &&
		(student.value.selected.option === "" ||
			!option.student_ids ||
			option.student_ids.length === 0 ||
			option.student_ids.includes(student.value.selected.option)) &&
		(!optionHasDeps ||
			(!employee.value.selected?.option &&
				!student.value.selected.option &&
				!department.value.selected.option) ||
			(optionHasDeps && depIncluded))
	);
};

const filteredThemes = (option) => {
	return (
		(option.type_id === props.tab ||
			props.tab === "all" ||
			option.option === 0) &&
		!option.deleted
	);
};

const filteredPlans = (option) => {
	return (
		!option.deleted &&
		(student.value.selected.option === "" ||
			!option.student_id ||
			option.student_id === student.value.selected.option)
	);
};

const departmentSelected = (selected) => {
	department.value.selected = selected;
	emit("department:selected", selected);
	emit("filterChanged");
};

const employeeSelected = (selected) => {
	employee.value.selected = selected;
	emit("employee:selected", selected);
	emit("filterChanged");
};

const studentSelected = (selected) => {
	student.value.selected = selected;
	emit("student:selected", selected);
	emit("filterChanged");
};

const categorySelected = (selected) => {
	category.value.selected = selected;
	emit("category:selected", selected);
	emit("filterChanged");
};

const planSelected = (selected) => {
	plan.value.selected = selected;
	emit("plan:selected", selected);
	emit("filterChanged");
};

const themeSelected = (selected) => {
	theme.value.selected = selected;
	emit("theme:selected", selected);
	emit("filterChanged");
};

const templateSelected = (selected) => {
	template.value.selected = selected;
	emit("template:selected", selected);
	emit("filterChanged");
};

const journalTypeSelected = (selected) => {
	emit("type:selected", selected);
	emit("filterChanged");
};

watchEffect(() => {
	if (props.students.length > 0 && student.value.selected.option !== "") {
		student.value.dep_ids =
			props.students
				.find(
					(studentToFind) =>
						studentToFind.student_id ===
						student.value.selected.option,
				)
				.department_ids.split(",")
				.map(Number) || [];
	} else {
		student.value.dep_ids = [];
	}
	if (departmentSelect.value) {
		emit("departmentLoaded", departmentSelect.value);
	}
	if (employeeSelect.value) {
		emit("employeeLoaded", employeeSelect.value);
	}
});
const getComponent = (filter) => {
	if (filter === "student" && props.studentId) return null;

	switch (filter) {
		case "department":
			return DepartmentSelect;
		case "employee":
			return EmployeeSelect;
		case "student":
			return StudentSelect;
		case "category":
			return Select;
		case "plan":
			return props.modulePermissions.isPlanModule ? Select : null;
		case "theme":
			return Select;
		case "template":
			return props.modulePermissions.isDataRegistrationModule
				? hasDepartments.value
					? CategorySelect
					: Select
				: null;
		case "type":
			return Select;
		default:
			return Select;
	}
};

const validFilters = computed(() => {
	return props.filters.filter((filter) => getComponent(filter) !== null);
});

const getModel = (filter) => {
	switch (filter) {
		case "department":
			return department.value;
		case "employee":
			return employee.value;
		case "student":
			return student.value;
		case "category":
			return category.value;
		case "plan":
			return plan.value;
		case "theme":
			return theme.value;
		case "template":
			return template.value;
		case "type":
			return journalType.value;
		default:
			return {};
	}
};

const getOptions = (filter) => {
	switch (filter) {
		case "department":
			return props.departmentOptions;
		case "employee":
			return props.employeeOptions;
		case "student":
			return props.studentOptions;
		case "category":
			return props.categoryOptions;
		case "plan":
			return props.planOptions;
		case "theme":
			return props.themeOptions;
		case "template":
			return props.templateOptions;
		case "type":
			return props.typeOptions;
		default:
			return [];
	}
};

const getLoading = (filter) => {
	switch (filter) {
		case "department":
			return department.value.isLoading;
		case "employee":
			return false;
		case "student":
			return props.studentsLoading;
		case "category":
			return props.categoriesLoading;
		case "plan":
			return props.plansLoading;
		case "theme":
			return props.themesLoading;
		case "template":
			return props.templatesLoading;
		case "type":
			return false;
		default:
			return false;
	}
};

const getSelectable = (filter) => {
	switch (filter) {
		case "department":
			return filteredDepartments;
		case "category":
			return filteredCategories;
		case "theme":
			return filteredThemes;
		case "plan":
			return filteredPlans;
	}
};

const getPlaceholder = (filter) => {
	switch (filter) {
		case "type":
			return "Vælg type";
		default:
			return "";
	}
};

const handleSelected = (filter, selected) => {
	switch (filter) {
		case "department":
			departmentSelected(selected);
			break;
		case "employee":
			employeeSelected(selected);
			break;
		case "student":
			studentSelected(selected);
			break;
		case "category":
			categorySelected(selected);
			break;
		case "plan":
			planSelected(selected);
			break;
		case "theme":
			themeSelected(selected);
			break;
		case "template":
			templateSelected(selected);
			break;
		case "type":
			journalTypeSelected(selected);
			break;
		default:
			break;
	}
};

const handleOpen = (filter) => {
	switch (filter) {
		case "student":
			emit("populateStudentOptions");
			break;
		case "category":
			emit("populateCategoryOptions");
			break;
		case "plan":
			emit("populatePlanOptions");
			break;
		case "theme":
			emit("populateThemeOptions");
			break;
		case "template":
			emit("populateTemplateOptions");
			break;
		default:
			break;
	}
};
const emit = defineEmits([
	"department:selected, employee:selected, student:selected, category:selected, plan:selected, theme:selected, template:selected, journalType:selected, departmentLoaded, employeeLoaded, populateStudentOptions, populateCategoryOptions, populatePlanOptions, populateThemeOptions, populateTemplateOptions, filterChanged",
]);
</script>
